import type {
  ActorDatabaseHandle,
  ActorTransaction,
} from "@-/dev.improve.haniverse.database.actor";
import { actorSchema } from "@-/dev.improve.haniverse.database.schemas.actor.schema";
import { addDays, format, startOfWeek } from "@-/com.npmjs.date-fns";
import { and, eq, gte, isNull, lt, sql } from "@-/com.npmjs.drizzle-orm";
import { EMPTY_HAN_REWARD, type HanReward } from "#han-reward";
import { hasRewardClaim } from "#read-han-wallet";

/**
 * The daily 红包 (red envelope), one per local day the app is opened, through a week-long cycle:
 * Han, then HanMiles, two bigger pots of Han, and a dragon to end the week. The user's
 * Sunday/Monday week-start setting defines the reset. Missed days shift later rewards back, so the
 * dragon requires a claim on all seven days of that calendar week. Day 6 pays 200 Han in place of
 * its bonus Han per review for now; a claim that holds a `reviewBonusHan` still pays it on every
 * review that day (`readReviewBonusHan`).
 */
export const DAILY_LOGIN_REWARD_CYCLE: readonly HanReward[] = [
  { ...EMPTY_HAN_REWARD, han: 10 },
  { ...EMPTY_HAN_REWARD, han: 20 },
  { ...EMPTY_HAN_REWARD, hanMiles: 1000 },
  { ...EMPTY_HAN_REWARD, hanMiles: 2500 },
  { ...EMPTY_HAN_REWARD, han: 100 },
  { ...EMPTY_HAN_REWARD, han: 200 },
  { ...EMPTY_HAN_REWARD, dragons: 1 },
];

/** The reward for the learner's `loginDayIndex`-th login day this week (0-based). */
export function dailyLoginRewardFor(loginDayIndex: number): HanReward {
  const reward = DAILY_LOGIN_REWARD_CYCLE[loginDayIndex % DAILY_LOGIN_REWARD_CYCLE.length];
  if (reward === undefined) {
    throw new Error(`No daily login reward for day ${loginDayIndex}`);
  }
  return reward;
}

/** The local calendar day a claim belongs to. */
export function rewardDayKey(now: Date): string {
  return format(now, "yyyy-MM-dd");
}

export type DailyLoginRewardDay = {
  dayKey: string;
  label: string;
  reward: HanReward | null;
  status: "collected" | "missed" | "unavailable" | "today" | "upcoming";
};

/** Calendar slots, with empty missed days and only the remaining reachable rewards. */
async function readDailyLoginRewardWeek(tx: ActorTransaction, now: Date) {
  const user = await tx.query.user.findFirst({ columns: { createdAt: true, weekStartsOn: true } });
  const start = startOfWeek(now, { weekStartsOn: user?.weekStartsOn === "sunday" ? 0 : 1 });
  const today = rewardDayKey(now);
  const claims = await tx
    .select()
    .from(actorSchema.rewardClaim)
    .where(
      and(
        isNull(actorSchema.rewardClaim.deletedAt),
        eq(actorSchema.rewardClaim.kind, "daily-login"),
        // Include the previous day to recognize a streak lost on the week's first day.
        gte(actorSchema.rewardClaim.claimedOn, rewardDayKey(addDays(start, -1))),
        lt(actorSchema.rewardClaim.claimedOn, rewardDayKey(addDays(start, 7))),
      ),
    );
  const loginDayIndex = claims.filter(
    (claim) => claim.claimedOn >= rewardDayKey(start) && claim.claimedOn < today,
  ).length;
  const createdDay = user === undefined ? today : rewardDayKey(new Date(user.createdAt));
  let upcomingIndex = loginDayIndex;
  const week: DailyLoginRewardDay[] = Array.from({ length: 7 }, (_, index) => {
    const date = addDays(start, index);
    const dayKey = rewardDayKey(date);
    const label = format(date, "EEE");
    const claim = claims.find((row) => row.claimedOn === dayKey);
    if (dayKey < today) {
      return {
        dayKey,
        label,
        reward:
          claim === undefined
            ? null
            : {
                dragons: claim.dragonAmount,
                han: claim.hanAmount,
                hanMiles: claim.hanMilesAmount,
                reviewBonusHan: claim.reviewBonusHan,
              },
        status:
          claim !== undefined
            ? "collected"
            : dayKey >= createdDay &&
                claims.some((row) => row.claimedOn === rewardDayKey(addDays(date, -1)))
              ? "missed"
              : "unavailable",
      };
    }
    const reward = dailyLoginRewardFor(upcomingIndex++);
    return { dayKey, label, reward, status: dayKey === today ? "today" : "upcoming" };
  });
  return { loginDayIndex, week };
}

export function dailyLoginRewardClaimId(dayKey: string): string {
  return `daily-login:${dayKey}`;
}

export interface ClaimedDailyLoginReward extends HanReward {
  /** 1-based position in the {@link DAILY_LOGIN_REWARD_CYCLE}. */
  cycleDay: number;
  dayKey: string;
  week: DailyLoginRewardDay[];
}

/**
 * Claims today's 红包. Returns `null` when today's was already claimed (on this device or, once
 * synced, another one: the id is the day, so a second claim cannot create a second row).
 */
export async function claimDailyLoginReward({
  actorDb,
  now,
}: {
  actorDb: ActorDatabaseHandle;
  now: Date;
}): Promise<ClaimedDailyLoginReward | null> {
  const dayKey = rewardDayKey(now);
  const id = dailyLoginRewardClaimId(dayKey);
  return actorDb.transaction(async (tx) => {
    if (await hasRewardClaim(tx, id)) {
      return null;
    }
    const { loginDayIndex, week } = await readDailyLoginRewardWeek(tx, now);
    const reward = dailyLoginRewardFor(loginDayIndex);
    const nowIso = now.toISOString();
    await tx
      .insert(actorSchema.rewardClaim)
      .values({
        claimedAt: nowIso,
        claimedOn: dayKey,
        createdAt: nowIso,
        dragonAmount: reward.dragons,
        hanAmount: reward.han,
        hanMilesAmount: reward.hanMiles,
        id,
        kind: "daily-login",
        reviewBonusHan: reward.reviewBonusHan,
      })
      .onConflictDoNothing()
      .run();
    return {
      ...reward,
      cycleDay: (loginDayIndex % DAILY_LOGIN_REWARD_CYCLE.length) + 1,
      dayKey,
      week,
    };
  });
}

/**
 * Extra Han a review committed at `now` earns: the review bonus of every live reward claimed on
 * that local day. Read inside the review's own transaction, so it is stored on the review row and
 * undoing the review takes it back.
 */
export async function readReviewBonusHan(tx: ActorTransaction, now: Date): Promise<number> {
  const [row] = await tx
    .select({ han: sql<number>`coalesce(sum(${actorSchema.rewardClaim.reviewBonusHan}), 0)` })
    .from(actorSchema.rewardClaim)
    .where(
      and(
        isNull(actorSchema.rewardClaim.deletedAt),
        eq(actorSchema.rewardClaim.claimedOn, rewardDayKey(now)),
      ),
    );
  return Number(row?.han ?? 0);
}

/** Whether today's 红包 is still unclaimed. */
export async function isDailyLoginRewardClaimable({
  actorDb,
  now,
}: {
  actorDb: ActorDatabaseHandle;
  now: Date;
}): Promise<{
  claimable: boolean;
  next: HanReward;
  nextCycleDay: number;
  week: DailyLoginRewardDay[];
}> {
  return actorDb.transaction(async (tx) => {
    const claimable = !(await hasRewardClaim(tx, dailyLoginRewardClaimId(rewardDayKey(now))));
    const { loginDayIndex, week } = await readDailyLoginRewardWeek(tx, now);
    return {
      claimable,
      next: dailyLoginRewardFor(loginDayIndex),
      nextCycleDay: (loginDayIndex % DAILY_LOGIN_REWARD_CYCLE.length) + 1,
      week,
    };
  });
}
